/**
 * Personal preferences for this browser (Settings page): theme, density, text size, motion, the
 * katana effects, the start page and the sidebar. Kept in localStorage, so each founder's choices
 * stay on their own computer. Storage can be unavailable (private windows), so every read and write
 * is guarded and the defaults always work.
 */
export type Theme = "system" | "light" | "dark";
export type Motion = "system" | "reduced" | "full";

export interface Prefs {
  theme: Theme;
  density: "comfortable" | "compact";
  text: "standard" | "large" | "largest";
  motion: Motion;
  katana: boolean;
  sidebarCollapsed: boolean;
  startPage: string;
  displayName: string;
  showCounts: boolean;
  language: string;
  /** "system" follows the computer's "more contrast" setting */
  contrast: "system" | "more";
  font: "brand" | "plain";
  spacing: "standard" | "wide";
  underlineLinks: boolean;
  readingGuide: boolean;
  /** Set once someone picks a theme themselves; until then light is the standard */
  themeChosen: boolean;
  /** The "What's new" card on Today, once dismissed */
  seenWhatsNew: string;
}

export const DEFAULT_PREFS: Prefs = {
  theme: "light",
  density: "comfortable",
  text: "standard",
  motion: "system",
  katana: true,
  sidebarCollapsed: false,
  startPage: "#/",
  displayName: "",
  showCounts: true,
  seenWhatsNew: "",
  language: "en",
  contrast: "system",
  font: "brand",
  spacing: "standard",
  underlineLinks: false,
  readingGuide: false,
  themeChosen: false,
};

const KEY = "ryoma.prefs.v1";
const RECENT_KEY = "ryoma.recent.v1";
type Listener = (p: Prefs) => void;
const listeners: Listener[] = [];

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the choice lasts until the page is reloaded.
  }
}

let current: Prefs = { ...DEFAULT_PREFS, ...read<Partial<Prefs>>(KEY, {}) };
// Light is the standard look: anyone who never picked a theme gets light, even if an older version saved "system".
if (!current.themeChosen) current.theme = "light";

export const prefs = (): Prefs => current;

export function setPrefs(patch: Partial<Prefs>): Prefs {
  current = { ...current, ...patch, ...(patch.theme ? { themeChosen: true } : {}) };
  write(KEY, current);
  applyPrefs();
  listeners.forEach((l) => l(current));
  return current;
}

export function resetPrefs(): Prefs {
  current = { ...DEFAULT_PREFS };
  write(KEY, current);
  applyPrefs();
  listeners.forEach((l) => l(current));
  return current;
}

export const onPrefs = (l: Listener): void => void listeners.push(l);

/** Puts the choices on <html> as data attributes; styles.css does the rest. */
export function applyPrefs(root: HTMLElement = document.documentElement): void {
  const p = current;
  const set = (name: string, value: string | null) => (value === null ? root.removeAttribute(name) : root.setAttribute(name, value));
  set("data-theme", p.theme === "system" ? null : p.theme);
  set("data-density", p.density === "compact" ? "compact" : null);
  set("data-text", p.text === "standard" ? null : p.text);
  set("data-contrast", p.contrast === "more" ? "more" : null);
  set("data-font", p.font === "plain" ? "plain" : null);
  set("data-spacing", p.spacing === "wide" ? "wide" : null);
  set("data-links", p.underlineLinks ? "underline" : null);
  set("data-motion", p.motion === "system" ? null : p.motion);
  set("data-katana", p.katana ? null : "off");
  set("data-sidebar", p.sidebarCollapsed ? "collapsed" : null);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", isDark() ? "#041528" : "#02203F");
}

/** The theme actually showing, after "system" is resolved. */
export function isDark(): boolean {
  if (current.theme !== "system") return current.theme === "dark";
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Whether animations should run: the app setting first, then the computer's. */
export function motionOk(): boolean {
  if (current.motion === "reduced") return false;
  if (current.motion === "full") return true;
  return !(typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
}

export const katanaOn = (): boolean => current.katana && motionOk();

// ---------- Recently viewed leads and audits, for the command palette ----------

export interface Recent { href: string; title: string; kind: "lead" | "audit"; at: number }

export const recent = (): Recent[] => read<Recent[]>(RECENT_KEY, []);

export function remember(item: Omit<Recent, "at">): void {
  const list = recent().filter((r) => r.href !== item.href);
  list.unshift({ ...item, at: Date.now() });
  write(RECENT_KEY, list.slice(0, 8));
}

export const forgetRecent = (): void => write(RECENT_KEY, []);
